import { useCreate, useList } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection"
import { IOrderAttendantItem, IOrderDetail } from "interfaces";
import { useEffect, useState } from "react"
import { AttendantCard } from "./card";
import Pagination from "components/pagination";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import Button from "components/button";
import { SearchIcon } from "@heroicons/react/outline";
import { isEmpty } from "lodash";

const ATTENDANT_PAGE_SIZE = 16;

export const ParticipantSection = ({orderDetailData} : {orderDetailData: IOrderDetail | null}) => {
  const { mutate, isLoading: isSendingEmail } = useCreate();
  
  const [trigger, setTrigger] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [filters, setFilters] = useState({
    page: "1",
    q: ""
  });

  const {
    data: participantData,
    refetch,
    isFetching,
  } = useList<IOrderAttendantItem>({
    resource: `checkout/orders/${orderDetailData?.code}/attendants`,
    pagination: {
      mode: "off",
      pageSize: ATTENDANT_PAGE_SIZE,
    },
    meta: {
      resource: "items",
      filters: [
        { field: "page", operator: "eq", value: filters.page },
        { field: "q", operator: "eq", value: filters.q }
      ],
    },
    queryOptions: {
      enabled: false,
    } as any,
  });

  useEffect(() => {
    if (trigger) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, filters]);

  const _sendAttendantEmail = (id: string) => {
    if (window.confirm(`Are you sure you want to send email to this attendant?`)) {
      mutate({
        resource: `checkout/orders/${orderDetailData?.code}/attendants/${id}/send-email`,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Sent successfully");
          refetch();
        },
      });
    }
  };

  
  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && { page: "1" }),
    };
    setFilters({
      ...filters,
      ...updatedFilter,
    });
  };

  return (
    <CollapsibleSection 
      label="Participant Information"
      defaultOpen={false}
      onClick={() => setTrigger(true)}
    >
      <div className="px-4 py-4 sm:px-6">
        <div className="w-full flex flex-col gap-y-2 md:flex-row md:items-end md:gap-x-3 md:gap-y-4">
          <div className="flex-1 md:max-w-md">
            <label htmlFor="name" className="block text-sm font-medium leading-6">
              Search
            </label>
            <div className="mt-2">
              <Input 
                className="px-2 py-2"
                label='Search'
                id='keyword'
                type="text"
                placeholder="Search by name, email, phone, reference number"
                value={keyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    _handleFilterChange("q", keyword);
                  }
                }}
              />
            </div>
          </div>      
          <div className="shrink-0">
            <label htmlFor="status" className="block text-sm font-medium leading-6">
              <span className="sr-only">Search</span>
            </label>
            <div className="mt-2 w-full">
              <Button 
                type="submit"
                className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85" 
                onClick={() => _handleFilterChange("q", keyword)}>
                <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-6">
          {isEmpty(participantData?.data) &&
          !isFetching && (
            <div className="text-center text-sm">
              No data
            </div>
          )}

          <AttendantCard 
            data={participantData?.data}
            orderDetailData={orderDetailData ?? null}
            _sendAttendantEmail={_sendAttendantEmail}
          />
        </div>
        {participantData?.paging && participantData.paging.total_page > 1 ? (
          <div className="mt-4 px-4 py-4 sm:px-6 flex justify-end items-center">
            <Pagination
              hideTotal
              currentPage={participantData?.paging.current_page}
              totalPage={participantData?.paging.total_page}
              total={{
                totalItems: participantData?.paging.total_item || 0,
                total: participantData?.paging.total,
              }}
              onChange={(page: number) =>
                _handleFilterChange("page", page)
              }
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      {isSendingEmail ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  )
}